/* Notification Center — the Delivery Method rules and labels, shared by the Delivery Method fields
 * (DeliveryMethodFields.tsx), Rule Detail and the New Rule wizard. The server re-checks every saved value
 * (DeliveryScheduler / RepeatPolicy); these only guide the user. */
import type { NcDelayMode } from './data';
import { RESULT_EXCEEDANCE_CATEGORY_CODE } from './categoryCodes';

/** The Result Exceedance (permit-limit) category's code — the only category Suppress duplicate firing
 *  applies to (owner decision 2026-10-19, hardcoded by code). */
export const PERMIT_EXCEEDANCE_CATEGORY_CODE = RESULT_EXCEEDANCE_CATEGORY_CODE;

/** Repeat-interval unit (owner request 2026-10-18: Hours or Minutes). */
export type RepeatUnit = 'Hours' | 'Minutes';
export const REPEAT_UNITS: RepeatUnit[] = ['Minutes', 'Hours'];

/** Delay as shown in the dropdown ⇄ as stored (AI_NotificationRule.DelayMode). */
export type DelayLabel = 'Immediate' | 'After 1 hour' | 'Daily digest';
export const DELAY_LABEL: Record<NcDelayMode, DelayLabel> = { Immediate: 'Immediate', After1Hour: 'After 1 hour', DailyDigest: 'Daily digest' };
export const DELAY_STORED: Record<DelayLabel, NcDelayMode> = { Immediate: 'Immediate', 'After 1 hour': 'After1Hour', 'Daily digest': 'DailyDigest' };
export const bizHoursLabel = (only: boolean) => (only ? 'Business hours only (Mon–Fri 08:00–17:00, not on lab holidays)' : 'Send any time (24/7)');

/** Everything the Delivery Method fields edit. `popupSetting` '' = follow the Default setting. */
export interface DeliveryValue {
  inApp: boolean;
  email: boolean;
  popupSetting: string;
  ack: boolean;
  repeat: { on: boolean; value: string; unit: RepeatUnit };
  delay: DelayLabel;
  bizHours: boolean;
  suppress: boolean;
  portal: boolean;
  autoResolve: boolean;
}

/** A new rule's delivery settings: the same defaults the server applies to a rule saved without them
 *  (Email on, In-App off, Immediate, any time, repeat every 4 hours when turned on), plus Requires
 *  acknowledgment on as the wizard has always started. */
export const NEW_RULE_DELIVERY: DeliveryValue = {
  inApp: false, email: true, popupSetting: '', ack: true,
  repeat: { on: false, value: '4', unit: 'Hours' },
  delay: 'Immediate', bizHours: false,
  suppress: true, portal: false, autoResolve: false,
};

/** Requires acknowledgment as it really applies: only to In-App alerts (an email cannot be acknowledged, and the
 *  server reads Ack only for the bell copies). With In-App unticked it is OFF — shown off, saved off, and no
 *  "Ack required" chip (owner report 2026-10-23: the chip showed with acknowledgment greyed out). */
export function effectiveAck(inApp: boolean | undefined, ack: boolean | undefined): boolean {
  return !!inApp && !!ack;
}

/** One line under Delay / Business hours saying what will actually happen (the server decides — this only
 *  describes the saved settings). */
export function delayNote(delay: DelayLabel, bizHours: boolean): string {
  const hours = bizHours ? ' An alert due outside business hours waits for the next opening (08:00 on a working day).' : '';
  if (delay === 'After 1 hour')
    return `Held for one hour, then re-checked: only what still meets the condition is sent.${hours}`;
  if (delay === 'Daily digest')
    return `Held alerts go out together at 08:00 as one email and one bell entry for this rule, re-checked first.${bizHours ? ' On weekends and lab holidays the digest waits for the next working day.' : ''}`;
  return bizHours ? `Sent as soon as raised during business hours.${hours}` : 'Sent as soon as the alert is raised.';
}

/** Repeat until acknowledged: why the interval is not usable, or null (the server's RepeatPolicy rule:
 *  a whole number 1–999, at least 5 minutes). */
export function repeatIntervalError(r: { value: string; unit: RepeatUnit }): string | null {
  const n = Number(r.value);
  if (!/^\d+$/.test(r.value) || n < 1 || n > 999) return 'Enter a whole number from 1 to 999.';
  if (r.unit === 'Minutes' && n < 5) return 'The shortest repeat is 5 minutes.';
  return null;
}
export const repeatLabel = (r: { on: boolean; value: string; unit: RepeatUnit }) => (r.on ? `Every ${r.value} ${r.unit}` : 'Off');

/** Repeat until acknowledged needs In-App + Requires acknowledgment (acknowledging stops it) and is not
 *  offered with a Daily digest (a digest goes out once a day by design). Null = available. */
export function repeatOffReason(v: Pick<DeliveryValue, 'inApp' | 'ack' | 'delay'>): string | null {
  if (!v.inApp) return 'Needs In-App (bell) ticked above — repeats stop when someone acknowledges in the bell.';
  if (!v.ack) return 'Needs Requires acknowledgment on — acknowledging is what stops the repeats.';
  if (v.delay === 'Daily digest') return 'Not available with a Daily digest delay — a digest is sent once a day.';
  return null;
}

/** Why the fields cannot be saved as they are, or null: a ticked repeat needs a usable interval. Checked
 *  whenever it is ticked, even while unavailable, because the server's RepeatPolicy.Validate refuses it
 *  then too (an unavailable repeat is stored, just never acted on). */
export function deliveryError(v: DeliveryValue): string | null {
  return v.repeat.on ? repeatIntervalError(v.repeat) : null;
}
